import type { Transition } from 'motion/react'

import { isAutomatedBrowser } from '@sparkai/utils'

export interface MotionPolicy {
  shouldAnimate: boolean
  instant: boolean
}

export interface MotionPolicyInput {
  prefersReducedMotion?: boolean
  isAutomated?: boolean
  isVisualTest?: boolean
  override?: MotionPolicyOverride
}

export const instantTransition: Transition = { duration: 0 }

export interface MotionPolicyOverride {
  instant?: boolean
}

let motionPolicyOverride: MotionPolicyOverride | undefined

export function setMotionPolicyOverride(override: MotionPolicyOverride | undefined) {
  motionPolicyOverride = override
}

function isVisualTestEnvironment() {
  if (globalThis.window === undefined) return false

  return Boolean(
    globalThis.window.location.search.includes('chromatic=true') ||
    globalThis.window.location.search.includes('visualTest=true') ||
    globalThis.window.localStorage.getItem('isChromatic') === 'true' ||
    globalThis.window.localStorage.getItem('isVisualTest') === 'true'
  )
}

export function getMotionPolicy(input: MotionPolicyInput = {}): MotionPolicy {
  const override = input.override ?? motionPolicyOverride

  if (override?.instant !== undefined) {
    return {
      shouldAnimate: !override.instant,
      instant: override.instant,
    }
  }

  const prefersReducedMotion =
    input.prefersReducedMotion ??
    globalThis.window?.matchMedia?.('(prefers-reduced-motion: reduce)').matches ??
    false

  const automated = input.isAutomated ?? isAutomatedBrowser
  const visualTest = input.isVisualTest ?? isVisualTestEnvironment()
  const instant = Boolean(prefersReducedMotion || automated || visualTest)

  return {
    shouldAnimate: !instant,
    instant,
  }
}

export function getPolicyTransition(policy: MotionPolicy, transition?: Transition): Transition | undefined {
  if (policy.instant) return instantTransition

  return transition
}

export function getPolicyVariants<T extends Record<string, unknown>>(
  policy: MotionPolicy,
  variants: { initial?: T; animate: T; exit?: T }
) {
  if (!policy.instant) return variants

  return {
    initial: variants.animate,
    animate: variants.animate,
    exit: variants.animate,
  }
}
